import * as React from 'react';
import { Query } from 'react-apollo';
import gql from 'graphql-tag';
import glamorous from 'glamorous';
import { RvLoader } from '../../components/Loader';
import { SearchCriteria } from '../SearchCriteria';
import CloseIcon from '@material-ui/icons/Close';
import { ChartCommonProps, IconButtonWrapper, ChartCommonState, HelpWrapper } from '../Common';
import { WithStyles, withStyles, IconButton } from '@material-ui/core';
import { styles } from '../styles';
import { ChartFilter } from '../ChartComponents';
import { formatNumber, getMaxTickLimit, getSuggestedMaxTick, convertStringNumberToNumber } from '../../helpers/ChartHelper';
import { SelectedItemProps } from '../MattersByStatusChart';
import { DrillDownType } from '../DrillDownType';
import { Bar, getElementAtEvent } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    Scale,
    Tick,
    Chart,
    ChartEvent,
    ActiveElement,
} from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import { ChartInfo } from '../../components/ChartInfo';

import { RV_CHART_CLASS, RV_CHART_COLORS, rvBarDatasetStyle, rvChartAnimation, rvCategoryAxisStyle, rvTooltipStyle, rvValueAxisStyle } from '../chartTheme';
import { ChartHeading } from '../ChartHeading';
import { AnimatedNumber } from '../AnimatedNumber';
import { ChartSkeleton } from '../../components/loading/ChartSkeleton';
import FolderOpenOutlinedIcon from '@material-ui/icons/FolderOpenOutlined';
ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    zoomPlugin
);

const BarWrapper = glamorous.div<{ theme?: object }>((props) => ({
    position: 'relative',
    paddingBottom: '10px',
    flex: 1,
    display: 'flex',
    flexFlow: 'column',
    maxWidth: '100%',
    maxHeight: '100%',
    height: '100%',
    '@media print': {
        display: 'flex',
        flexFlow: 'column',
        alignItems: 'center',
    }
}));

const BarTitleContainer = glamorous.div<{ theme?: object }>((props) => ({
    display: 'flex',
    flexFlow: 'column',
    alignItems: 'center',
    padding: '15px 15px 0px 15px',
    fontSize: '16px',
    fontWeight: 600,
    [`& .${`h1`}`]: {
        display: 'flex',
        alignItems: 'center',
    },
    [`& .${`h2`}`]: {
        fontSize: '2.5rem',
        padding: '10px 0',
    },
})).withProps({ className: 'rv-chart-title-area' });

const BarContainer = glamorous.div<{ theme?: object }>((props) => ({
    padding: '5px 15px 15px 15px',
    minHeight: '300px',
    flex: 1,
    display: 'flex',
    flexFlow: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    maxHeight: '350px',
    maxWidth: 'auto',
    position: 'relative',
    '@media (max-width: 533px)': {
        maxWidth: '350px',
        margin: '0 auto',
    },
    [`& .${`titleWrapper`}`]: {
        textAlign: 'center',
        display: 'inline-block',
        width: '100%',
        padding: '15px',
        [`& .${`title`}`]: {
            fontSize: '18px',
            fontWeight: 600,
            display: 'block',
            marginBottom: '15px',
        },
        [`& .${`value`}`]: {
            fontSize: '24px',
            fontWeight: 600,
            display: 'block',
            marginBottom: '15px',
        },
        [`& .${`secondary-title`}`]: {
            fontSize: '16px',
            fontWeight: 400,
            display: 'block',
            marginBottom: '15px',
        }
    }
}));

const LoadWrapper = glamorous.div<{ theme?: object }>((props) => ({
    padding: '20px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
}));

const TotalDiv = glamorous.div<{ theme?: object }>((props) => ({
    cursor: 'pointer',
    paddingRight: 5
}));

interface ChartProps extends SelectedItemProps, ChartCommonProps, WithStyles<typeof styles> {
    chartFilter?: ChartFilter;
}

interface ChartState extends ChartCommonState {
    // 
}

class MatterActivityChart extends React.Component<ChartProps, ChartState>  {

    // tslint:disable-next-line: no-any
    public chartRef: React.RefObject<any>;

    constructor(props: ChartProps, state: ChartState) {
        super(props, state);
        this.onCloseChart = this.onCloseChart.bind(this);
        this.handlePopoverOpen = this.handlePopoverOpen.bind(this);
        this.handlePopoverClose = this.handlePopoverClose.bind(this);
        this.handleTotalClick = this.handleTotalClick.bind(this);
        this.onHover = this.onHover.bind(this);

        this.onPan = this.onPan.bind(this);

        this.chartRef = React.createRef();

        this.state = {
            open: false,
            anchorEl: undefined
        };
    }

    public componentDidMount() {
        // 
    }

    public componentWillUnmount() {
        // 
    }

    public shouldComponentUpdate(nextProps: ChartProps, nextState: ChartState): boolean {
        if (this.props !== nextProps || this.state !== nextState) {
           return true;
        }

        return false;
    }

    public render() {

        return (
            <React.Fragment>
                {/* tslint:disable-next-line: no-any */}
                <Query<any> 
                    query={ChartData}
                    variables={{
                        entityType: this.props.chartFilter?.entityType,
                        entityGuid: this.props.chartFilter?.entityGuid,
                        responsibility: this.props.chartFilter?.responsibility
                    }}
                >
                    {({ loading, error, data }) => {

                        if (loading) {
                            return (
                                <ChartSkeleton variant="bars" />
                            );
                        }

                        const statistics = data.statistics;

                        let matter = (loading || error ||
                            statistics === null || statistics === undefined)
                            ? null
                            : statistics.matter;

                        let matterActivityData = matter ?
                            matter.matterActivityData : null;

                        if (matterActivityData) {
                            const {
                                closedMattersCount,
                                currentMattersCount
                            } = matterActivityData;

                            const { lessThan30, from31To60, from61To90, greaterThan90 } = matterActivityData.matterActivity;

                            return (
                                <BarWrapper className={RV_CHART_CLASS}>
                                    <IconButtonWrapper
                                        aria-label="close"
                                    >
                                        <HelpWrapper className="rv-chart-help">
                                            <ChartInfo 
                                                description={this.props.description}
                                            />
                                        </HelpWrapper>
                                        <IconButton
                                            aria-label="close"
                                            onClick={this.onCloseChart}
                                            size="small"
                                        >
                                            <CloseIcon />
                                        </IconButton>
                                    </IconButtonWrapper>
                                    {/* tslint:disable-next-line: no-any */}
                                    <Query<any> query={CurrencySymbol} fetchPolicy="cache-first">
                                        {({
                                            // tslint:disable-next-line: no-shadowed-variable
                                            loading, error, data
                                        }) => {

                                            if (loading) {
                                                return (
                                                    <LoadWrapper className="load-wrapper">
                                                        <RvLoader size="small" />
                                                    </LoadWrapper>
                                                );
                                            }

                                            // const currencySymbol = (loading || error) ?
                                            //    null : data.options.currencySymbol;

                                            // const symbol = currencySymbol ? currencySymbol.symbol : null;

                                            return (
                                                <BarTitleContainer>
                                                    <ChartHeading icon={<FolderOpenOutlinedIcon />} title="Current Matters" />
                                                    <TotalDiv className="h2" onClick={() => this.handleTotalClick(true)}>
                                                        <AnimatedNumber value={currentMattersCount} format={(amount) => formatNumber(amount)} />
                                                    </TotalDiv>
                                                    <div className="h3">
                                                        <TotalDiv onClick={() => this.handleTotalClick(false)}>
                                                            {formatNumber(closedMattersCount)} 
                                                        </TotalDiv>                                                        
                                                        Closed Matter(s)
                                                    </div>
                                                    <div className="h3">Current Matters Last Active</div>
                                                </BarTitleContainer>
                                            );
                                        }}
                                    </Query>
                                    <BarContainer className="rv-chart-bars">
                                        {/* tslint:disable-next-line: no-any */}
                                        <Query<any> query={CurrencySymbol}>
                                            {({
                                                // tslint:disable-next-line: no-shadowed-variable
                                                loading, error, data
                                            }) => {

                                                if (loading) {
                                                    return (
                                                        <LoadWrapper className="load-wrapper">
                                                            <RvLoader size="small" />
                                                        </LoadWrapper>
                                                    );
                                                }

                                                // const currencySymbol = (loading || error) ?
                                                //     null : data.options.currencySymbol;

                                                // const symbol = currencySymbol ? currencySymbol.symbol : null;
                                                return (
                                                    <Bar
                                                        ref={this.chartRef}
                                                        // tslint:disable-next-line: no-any
                                                        onClick={(event: React.MouseEvent<any>) => {
                                                            const _data = getElementAtEvent(this.chartRef.current, event);
                                                            if (_data.length > 0 && this.props.getSelectedItem) {
            
                                                                const _index = getElementAtEvent(this.chartRef.current, event)[0].index;
            
                                                                const item = ['Greater Than 90 days', '61-90 Days ago', '31-60 Days ago', 'Last 30 Days'][_index];

                                                                const searchCriterias = new Array<SearchCriteria>();

                                                                if (item === 'Last 30 Days') {
                                                                    searchCriterias.push(
                                                                        { property: 'LastUpdateStartByPeriod', operator: 'lt', comparison: '0' },
                                                                        { property: 'LastUpdateEndByPeriod', operator: 'gteq', comparison: '30' }
                                                                    );
                                                                } else if (item === '31-60 Days ago') {
                                                                    searchCriterias.push(
                                                                        { property: 'LastUpdateStartByPeriod', operator: 'lt', comparison: '30' },
                                                                        { property: 'LastUpdateEndByPeriod', operator: 'gteq', comparison: '60' }
                                                                    );
                                                                } else if (item === '61-90 Days ago') {
                                                                    searchCriterias.push(
                                                                        { property: 'LastUpdateStartByPeriod', operator: 'lt', comparison: '60' },
                                                                        { property: 'LastUpdateEndByPeriod', operator: 'gteq', comparison: '90' }
                                                                    );
                                                                } else if (item === 'Greater Than 90 days') {
                                                                    searchCriterias.push(
                                                                        { property: 'LastUpdateStartByPeriod', operator: 'lt', comparison: '90' }
                                                                    );
                                                                }

                                                                searchCriterias.push(
                                                                    { property: 'InProgress', operator: 'eq', comparison: '1' }
                                                                );

                                                                this.props.getSelectedItem(
                                                                    searchCriterias,
                                                                    true,
                                                                    'Matter Activity',
                                                                    DrillDownType.matterSummaryDrilldown,
                                                                    'Last Updated'
                                                                );
                                                            }
                                                        }}
                                                        data={{
                                                            labels: ['Greater Than 90 days', '61-90 Days ago', '31-60 Days ago', 'Last 30 Days'],
                                                            datasets: [
                                                                {
                                                                    label: 'Matters',
                                                                    ...rvBarDatasetStyle([RV_CHART_COLORS.red, RV_CHART_COLORS.amber, RV_CHART_COLORS.primary, RV_CHART_COLORS.primarySoft]),
                                                                    data: [greaterThan90, from61To90, from31To60, lessThan30]
                                                                }
                                                            ]
                                                        }}
                                                        options={{
                                                            onHover: this.onHover,
                                                            indexAxis: 'y' as const,
                                                            animation: rvChartAnimation(),
                                                            elements: { bar: { borderWidth: 0 } },
                                                            responsive: true,
                                                            maintainAspectRatio: false,
                                                            scales: {
                                                              x: {
                                                                grid: rvValueAxisStyle.grid,
                                                                // beginAtZero: true,
                                                                suggestedMax: getSuggestedMaxTick([greaterThan90, from61To90, from31To60, lessThan30]),
                                                                beginAtZero: true,
                                                                ticks: {
                                                                    ...rvValueAxisStyle.ticks,
                                                                    maxTicksLimit: getMaxTickLimit([greaterThan90, from61To90, from31To60, lessThan30]),
                                                                    autoSkip: false,
                                                                    callback: function (this: Scale, tickValue: number, index: number, ticks: Tick[]) {
                                                                        return `${formatNumber(tickValue)}`;
                                                                    }
                                                                }
                                                              },
                                                              y: {
                                                                grid: rvCategoryAxisStyle.grid,
                                                                // min: 0,
                                                                // max: 9 // minimum display
                                                                ticks: {
                                                                    ...rvCategoryAxisStyle.ticks,
                                                                    autoSkip: false
                                                                }
                                                              }
                                                            },
                                                            plugins: {
                                                              legend: {
                                                                position: 'bottom' as const,
                                                                display: false
                                                              },
                                                              title: {
                                                                display: false,
                                                                text: ``,
                                                              },
                                                              zoom: {
                                                                pan: {
                                                                  enabled: false,
                                                                  onPan: this.onPan
                                                                },
                                                                zoom: {
                                                                  wheel: {
                                                                    enabled: false
                                                                  }
                                                                }
                                                                // limits: {
                                                                //   y: { min: 0, max: 9 },
                                                                // },
                                                              },
                                                              tooltip: {
                                                                ...rvTooltipStyle,
                                                                callbacks: {
                                                                    // tslint:disable-next-line: no-any
                                                                    label: function (context: any) {
                                                                        const { 
                                                                            //   dataIndex, 
                                                                            //   label, 
                                                                            formattedValue,
                                                                            // dataset 
                                                                        } = context;
                
                                                                        var currentValue = convertStringNumberToNumber(formattedValue);
        
                                                                        return `${formatNumber(currentValue)}`;
                                                                        
                                                                    },
                                                                }
                                                              }
                                                            },
                                                        }}
                                                    />
                                                );
                                            }}
                                        </Query>
                                    </BarContainer>
                                </BarWrapper>
                            );
                        }

                        return (
                            <LoadWrapper className="load-wrapper">
                                <RvLoader size="small" />
                            </LoadWrapper>
                        );
                    }}
                </Query>
            </React.Fragment>
        );
    }

    private handleTotalClick(isCurrentMatters: boolean) {
        if (this.props.getSelectedItem) {
            const searchCriterias = new Array<SearchCriteria>();
            searchCriterias.push(
                { property: 'InProgress', operator: 'eq', comparison: isCurrentMatters ? '1' : '0' }
            );

            this.props.getSelectedItem(
                searchCriterias,
                true,
                'Matter Activity',
                DrillDownType.matterSummaryDrilldown,
                'Last Updated'
            );
        }        
    }

    private onCloseChart() {
        if (this.props.onCloseChart) {
            return this.props.onCloseChart('isMatterActivityChartOpen');
        }
    }

    // tslint:disable-next-line: no-any
    private handlePopoverOpen(event: any) {
        this.setAnchorEl(event.currentTarget);
    }

    private handlePopoverClose = () => {
        this.setAnchorEl(null);
    }

    // tslint:disable-next-line: no-any
    private setAnchorEl(anchorElValue: any) {
        this.setState({
            anchorEl: anchorElValue,
            open: Boolean(anchorElValue)
        });
    }

    private onPan = (context: { chart: Chart }) => {
        this.chartRef.current.options.scales.x.min = 0;
        this.chartRef.current.update();
    }

    private onHover(event: ChartEvent, elements: ActiveElement[], chart: Chart) {
        (event?.native?.target as HTMLElement).style.cursor = elements.length > 0 ? 'pointer' : 'auto';
    }
}

// tslint:disable-next-line: no-any
export default withStyles(styles as any, { withTheme: true })(MatterActivityChart as any) as any;

const ChartData = gql`
query MatterActivityData($entityType: Int, $entityGuid: String, $responsibility: Int) {
    statistics {
      matter {
        matterActivityData(entityType: $entityType, entityGuid: $entityGuid, responsibility: $responsibility) {
          matterActivity {
            lessThan30
            from31To60
            from61To90
            greaterThan90
          }
          currentMattersCount
          closedMattersCount
        }
      }
    }
  }
  `;

const CurrencySymbol = gql`query CurrencySymbol {
    options {currencySymbol {symbol}}
}`;